import type { ParsedLocation } from "@tanstack/react-router";

/**
 * Which way a navigation travels, as a view transition type.
 *
 * Screens are a stack of cards. Opening a scenario from the store deals a card
 * onto the store; opening another from *its* similar rail deals a card onto
 * that one. Going back takes the top card off, and what is underneath is
 * whatever you were last looking at rather than the front of the store — which
 * the browser's own history already models, so nothing here keeps a stack.
 */

/**
 * How deep into the app a path is. Opening something always goes deeper.
 *
 * Everything the ways offer is at the front: they are destinations you change
 * between, not places you step into, so moving between them should look like
 * nothing is opening or closing. A screen that is also reachable from inside
 * another one can still ask for the deeper movement where that is what it is
 * — see the store's search link.
 *
 * Starting a game and playing it are one card: the new-game screen is the
 * game's own entry curtain, waiting on the game to exist.
 */
function depthOf(location: ParsedLocation): number {
  const { pathname } = location;
  const search = location.search as Record<string, unknown>;
  if (pathname === "/evals")
    return search.runId || search.caseId ? 2 : search.evalId || search.draft ? 1 : 0;
  if (pathname === "/agents") return search.agentId ? 1 : 0;
  if (pathname.startsWith("/play/") || /^\/presets\/[^/]+\/new\/?$/.test(pathname)) return 3;
  if (pathname.startsWith("/presets/")) return 2;
  // The library is a way; your assets and the collections are rooms off it,
  // an asset or a collection is a card dealt from one of those, and its
  // editor is opened from the asset.
  if (/^\/assets\/[^/]+\/edit\/?$/.test(pathname)) return 3;
  if (/^\/assets\/(mine|collections)\/?$/.test(pathname)) return 1;
  if (pathname.startsWith("/assets/")) return 2;
  return 0;
}

/**
 * Where a location sits in session history. Only consulted between two screens
 * at the same depth, where depth alone cannot say which way you are going:
 * one scenario to another is forward through a similar rail and back through
 * the browser, and the paths look identical either way. A screen that replaces
 * itself keeps its place, and so does not travel at all.
 */
function historyIndex(location: ParsedLocation): number {
  const index = location.state.__TSR_index;
  return typeof index === "number" ? index : 0;
}

/**
 * Whether the browser animated the travel itself, as a swipe from the edge of
 * a phone or two fingers on a trackpad does. The gesture moves the screens
 * under the finger and lands on the destination fully arrived, so a card dealt
 * on top of it would travel a second time over something that has already
 * finished moving. Pressing the chevron, the browser's own back button, or a
 * link leaves this false, and those still travel.
 */
let browserTravelled = false;

window.addEventListener("popstate", event => {
  browserTravelled = event.hasUAVisualTransition;
});

export function screenTravel(change: {
  fromLocation?: ParsedLocation;
  toLocation: ParsedLocation;
}): Array<string> | false {
  const gestured = browserTravelled;
  browserTravelled = false;
  if (gestured) return false;

  const { fromLocation, toLocation } = change;
  if (!fromLocation) return false;
  const from = depthOf(fromLocation);
  const to = depthOf(toLocation);
  if (fromLocation.pathname === toLocation.pathname && from === to) {
    const keys = ["evalId", "caseId", "runId", "agentId", "draft"];
    if (
      keys.every(
        key =>
          (fromLocation.search as Record<string, unknown>)[key] ===
          (toLocation.search as Record<string, unknown>)[key],
      )
    )
      return false;
  }
  if (to > from) return ["forward"];
  if (to < from) return ["back"];
  // Both at the front of the app: the store, your games, signing in. Nothing
  // is opening or closing, so nothing should look like it is.
  if (to === 0) return ["peer"];
  const fromIndex = historyIndex(fromLocation);
  const toIndex = historyIndex(toLocation);
  if (toIndex === fromIndex) return false;
  return [toIndex < fromIndex ? "back" : "forward"];
}
